/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import can from 'can';
import template from './player-comparison-pill.stache';
import PlayerService from 'scripts/services/player-service';
import device from 'scripts/utils/device';
import appContext from 'scripts/app-context';
import _ from 'lodash';
import { sendPlayerComparisonSelectionAnalytics } from './controllers/analytics.controller';

const DESCRIPTION =
    'Select two players from the list above to compare statistics.';
const TITLE_LINK = 'Compare players';
const DEFAULT_PLAYER_MODEL = {
    headshot: null,
    isFetching: false,
};

const playerComparisonUrl = window.globalSettings.pagePaths.playerComparison;

const isComparisonPage = () => {
    const comparisonHref = playerComparisonUrl.split('#')[0];
    return location.href.indexOf(comparisonHref) !== -1;
};

can.Component.extend({
    tag: 'player-comparison-pill',
    template: template,
    leakScope: false,
    viewModel: {
        description: DESCRIPTION,
        linkTitle: TITLE_LINK,
        leftPlayer: DEFAULT_PLAYER_MODEL,
        rightPlayer: DEFAULT_PLAYER_MODEL,
        define: {
            leftPlayerId: {
                type: 'string',
                value: null,
            },
            leftHeadshot: {
                get() {
                    const playerId = this.attr('leftPlayerId');
                    return this.getHeadshot(playerId);
                },
            },
            rightPlayerId: {
                type: 'string',
                value: null,
            },
            rightHeadshot: {
                get() {
                    const playerId = this.attr('rightPlayerId');
                    return this.getHeadshot(playerId);
                },
            },
            isCompareReady: {
                get() {
                    return (
                        Boolean(this.attr('leftPlayerId')) &&
                        Boolean(this.attr('rightPlayerId'))
                    );
                },
            },
            playerList: {
                Type: can.List,
                Value: can.List,
            },
        },
        getHeadshot(playerId) {
            const playerData = this.attr('playerList')
                .serialize()
                .find((playerData) => playerData.identifier == playerId);

            if (playerData) {
                return playerData.image;
            }

            return undefined;
        },
        applyCompare() {
            if (!this.attr('isCompareReady')) {
                return;
            }

            const id1 = this.attr('leftPlayerId');
            const id2 = this.attr('rightPlayerId');

            const urlOptions = { player1: id1, player2: id2 };

            const comparisonPageUrl = can.sub(playerComparisonUrl, urlOptions);

            if (isComparisonPage()) {
                window.location = comparisonPageUrl;
            } else {
                window.open(comparisonPageUrl, '_blank');
            }

            this.dispatch('applyCompare');
            sendPlayerComparisonSelectionAnalytics(
                [id1, id2],
                this.attr('playerList'),
            );
        },
        resetLeftPlayer() {
            const playerId = this.attr('leftPlayerId');
            this.dispatch('resetPlayer', [playerId]);
        },
        resetRightPlayer() {
            const playerId = this.attr('rightPlayerId');
            this.dispatch('resetPlayer', [playerId]);
        },
        onResetLeftPlayer(event) {
            event.stopPropagation();
            this.resetLeftPlayer();
        },
        onResetRightPlayer(event) {
            event.stopPropagation();
            this.resetRightPlayer();
        },
    },
    events: {
        inserted() {
            PlayerService.getList((ev, data) =>
                this.viewModel.attr('playerList', data),
            );

            appContext.model.screen.bind(
                'screenDimensions',
                _.debounce(this.handlePillPosition, 200),
            );
            this.handlePillPosition();
        },
        handlePillPosition() {
            const offset = device.getIOSBannerOffset();
            const transformEntry = `translate(-50%, -${offset}px)`;

            can.$('.c-player-comparison-pill').css({
                transform: transformEntry,
            });
        },
        removed() {
            if (this.leftPlayerFeedService) {
                this.leftPlayerFeedService.destroy();
                this.leftPlayerFeedService = null;
            }

            if (this.rightPlayerFeedService) {
                this.rightPlayerFeedService.destroy();
                this.rightPlayerFeedService = null;
            }
        },
    },
});
